import React, { useState, useEffect } from 'react';
import { Box, Divider, Button, useMediaQuery, Container, Menu, MenuItem } from '@mui/material';
import { 
  ChatBubbleOutline, 
  PeopleOutlineOutlined, 
  PersonOutlineSharp, 
  NotificationsOutlined, 
  FolderOutlined, 
  AddCircleOutlineOutlined,
  Settings,
  HelpOutline,
  InfoOutlined,
  FavoriteOutlined
} from '@mui/icons-material';
import StickyBox from "react-sticky-box";
import { Link, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../Auth/FirebaseAuth';
import { useCallStatus } from '../Context/CallContext';

const navLinkStyles = {
  display: 'flex',
  alignItems: 'center',
  textTransform: 'none',
  fontSize: '1.3rem',
  fontWeight: '500',
  color: '#111827',
  borderRadius: 1,
  px: 2,
  py: 1,
  justifyContent: 'flex-start',
  /*'&:hover': {
    color: '#fff',
    backgroundColor: '#4f46e5',
  },*/
};

const navIconStyles = {
  fontSize: '2rem',
  mr: 1,
};



// Regular navigation items (exclude More from links)
const navItems = [
  {
    label: 'Home',
    icon: (
      <Box
        component="img"
        src="/home.png"
        alt="Home"
        sx={{ width: '2rem', mr: 1, flexShrink: 0 }}
      />
    ),
    link: '/',
  },
  /*{
    label: 'Explore Issues',
    icon: <ChatBubbleOutline sx={navIconStyles} />,
    link: '/explore',
  },*/
  /*{
    label: 'Neighbors',
    icon: <PeopleOutlineOutlined sx={navIconStyles} />,
    link: '/neighbors',
  },*/
  {
    label: 'Profile',
    icon: <PersonOutlineSharp sx={navIconStyles} />,
    link: '/profile',
  },
  // Add this new item
  {
    label: 'Support Us',
    icon: <FavoriteOutlined sx={navIconStyles} />,
    link: 'https://explore.unitedintalk.com/supportus',
    isExternal: true, // Flag this as external link
  },
  /*{
    label: 'Notifications',
    icon: <NotificationsOutlined sx={navIconStyles} />,
    link: '/notifications',
  },
  {
    label: 'Log',
    icon: <FolderOutlined sx={navIconStyles} />,
    link: '/log',
  },*/
];

interface DropdownItem {
  label: string;
  icon: React.ReactNode;
  link?: string;
  isLogout?: boolean;
  isExternal?: boolean;
}

// Dropdown menu items
const dropdownItems: DropdownItem[] = [
  //{ label: 'Settings', icon: <Settings fontSize="small" />, link: '/settings' },
  { label: 'Terms of Use', icon: <InfoOutlined fontSize="small" />, link: '/terms' },
  { label: 'Help & Support', icon: <HelpOutline fontSize="small" />, link: 'https://explore.unitedintalk.com/contact', isExternal: true },
  //{ label: 'About', icon: <InfoOutlined fontSize="small" />, link: '/about' },
  { label: 'Logout', icon: <LogoutIcon fontSize="small" />, isLogout: true },
  
];

const Navbar = () => {
  const isLargeScreen = useMediaQuery('(min-width:1400px)');
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { inCall } = useCallStatus();
  const [supportClicked, setSupportClicked] = useState(false);
  
  // Load visited state from localStorage on component mount
  useEffect(() => {
    const hasVisitedSupport = localStorage.getItem('visitedSupportPage') === 'true';
    setSupportClicked(hasVisitedSupport);
  }, []);
  
  // Handle support button click
  const handleSupportClick = () => {
    localStorage.setItem('visitedSupportPage', 'true');
    setSupportClicked(true);
    window.open('https://explore.unitedintalk.com/supportus', '_blank');
  };

  const handleLogout = async () => {
    try {
      await logout(); // Call the Firebase logout function
      handleClose(); // Close the dropdown
      navigate('/login'); // Redirect to login page
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Function to determine if a nav item is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isLargeScreen ? '500px' : '72px',
          minWidth: '50px',
          maxWidth: '300px',
          position: 'sticky',
          top: '71px',
          backgroundColor: '#f5f5f5',
          height: 'calc(100vh - 71px)',
          overflow: 'auto',
          alignSelf: 'flex-start',
          zIndex: 90,
          overflowX: 'hidden',
        }}
      >
      {/* Logo */}
          <Box sx={{ p: 0, display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Box
          component="img"
          
          src={isLargeScreen ? "/UnitedInTalkLogo_Black.svg" : "/UnitedInTalk_Logo_Flag.svg"}
          alt="Logo"
          sx={{ 
            width: '70%', 
            maxWidth: '30rem', 
            height: 'auto', 
            display: 'block', 
            objectFit: 'contain',
            margin: 'left',
            pl: 2,
            pt: 2,
            cursor: 'pointer', // Add pointer cursor to indicate it's clickable
            transition: 'opacity 0.2s ease',
            /*'&:hover': {
              opacity: 0.85, // Subtle hover effect
            },*/
          }}
        />
      </Link>
    </Box>
      
      {/* Navigation Links */}
      <Box sx={{ flexGrow: 1, px: 1, mt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Regular nav items */}
          {navItems.map((item, idx) => {
  // Determine the button structure based on type
  if (item.label === 'Support Us') {
    // Support Us button with click handler
    return (
      <Button
        key={idx}
        onClick={handleSupportClick}
        variant="text"
        sx={{
          ...navLinkStyles,
          justifyContent: isLargeScreen ? 'flex-start' : 'center',
          color: supportClicked ? '#111827' : '#e91e63',
          //transition: 'color 0.3s ease',
        }}
        disabled={inCall}
      >
        {item.icon}
        {isLargeScreen && item.label}
      </Button>
    );
  } else if (item.isExternal) {
    // External link uses anchor element
    return (
      <Button
        key={idx}
        component="a"
        href={item.link}
        target="_blank"
        rel="noopener noreferrer"
        variant="text"
        sx={{
          ...navLinkStyles,
          justifyContent: isLargeScreen ? 'flex-start' : 'center',
        }}
        disabled={inCall}
      >
        {item.icon}
        {isLargeScreen && item.label}
      </Button>
    );
  } else {
    // Internal link uses Link component
    return (
      <Button
        key={idx}
        component={Link}
        to={item.link}
        variant="text"
        sx={{
          ...navLinkStyles,
          justifyContent: isLargeScreen ? 'flex-start' : 'center',
          ...(isActive(item.link) && {
            color: '#4f46e5',
            fontWeight: 'bold',
          }),
        }}
        disabled={inCall}
      >
        {item.icon}
        {isLargeScreen && item.label}
      </Button>
    );
  }
})}
          
          {/* More button with dropdown */}
          <Button
            id="more-button"
            
            aria-controls={open ? 'more-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleMoreClick}
            variant="text"
            sx={{
              ...navLinkStyles,
              justifyContent: isLargeScreen ? 'flex-start' : 'center',
              ...(open && {
                color: '#4f46e5',
                fontWeight: 'bold',
              }),
            }}
          >
            <AddCircleOutlineOutlined sx={navIconStyles} />
            {isLargeScreen && 'More'}
          </Button>
          
          <Menu
          id="more-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'more-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              mt: 0.5, // Small margin top for visual separation
              boxShadow: '0px 5px 15px rgba(0,0,0,0.15)',
            }
          }}
        >
          {dropdownItems.map((item, idx) => (
          <MenuItem 
          key={idx} 
          component={item.isLogout ? 'button' : item.isExternal ? 'a' : Link} 
          to={!item.isLogout && !item.isExternal ? item.link : undefined}
          href={item.isExternal ? item.link : undefined}
          target={item.isExternal ? "_blank" : undefined}
          rel={item.isExternal ? "noopener noreferrer" : undefined}
          onClick={item.isLogout ? handleLogout : handleClose}
          sx={{
            fontSize: '1rem',
            display: 'flex',
            gap: 1,
            minWidth: '180px',
            // Add null check - only call isActive if item.link exists
            ...(item.link && isActive(item.link) && !item.isLogout && !item.isExternal && {
              color: '#4f46e5',
              fontWeight: 'bold',
            }),
          }}
        >
          {item.icon}
          {item.label}
        </MenuItem>
        ))}
        </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default Navbar;